import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import '../css/Reviews.css';

const ReviewsCarousel = () => {
    const [current, setCurrent] = useState(0);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Shuffles an array into a random order (Fisher–Yates algorithm)
    // Returns a NEW array without mutating the original
    const shuffleArray = (array) => {

        // Create a shallow copy so we don't modify the original array
        const shuffled = [...array];

        // Start from the end of the array and move backward
        for (let i = shuffled.length - 1; i > 0; i--) {

            // Pick a random index from 0 to i (inclusive)
            const j = Math.floor(Math.random() * (i + 1));

            // Swap the element at index i with the element at index j
            // Array destructuring allows this swap in one line
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        // Return the randomly shuffled array
        return shuffled;
    };


    // Fetch reviews from JSON
    useEffect(() => {
        // Start an asynchronous fetch to load the reviews.json file from the public folder
        fetch('/json/reviews.json')
            .then((res) => {
                // Check if the response is NOT OK (e.g., 404 or 500 error)
                if (!res.ok) throw new Error('Failed to load reviews');
                // If OK, parse the response body as JSON
                return res.json();
            })
            .then((data) => {
                // On successful parsing, update the reviews state with the fetched data
                setReviews(shuffleArray(data)); // ⭐ reshuffle every load
                // Set the loading state to false (done loading)
                setLoading(false);
            })
            .catch((err) => {
                // If there was an error (network or parsing), store the error message
                setError(err.message);
                // Still mark loading as false to stop any spinners or loading indicators
                setLoading(false);
            });
        // Empty dependency array means this effect runs ONLY ONCE after the initial render (like componentDidMount)
    }, []);


    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const visibleCount = windowWidth < 768 ? 1 : 2;
    const slideWidth = 400;
    const prev = () => setCurrent((current - 1 + reviews.length) % reviews.length);
    const next = () => setCurrent((current + 1) % reviews.length);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrent((prev) => (prev + 1) % reviews.length);
        }, 7000);
        return () => clearInterval(interval);
    }, [reviews.length]);

    const displayedReviews = reviews.length > 1
        ? reviews.concat(reviews.slice(0, visibleCount))
        : reviews;

    // ⭐ Render stars based on rating value
    const renderStars = (rating) => '★'.repeat(rating) + '☆'.repeat(5 - rating);

    const total = reviews.length;
    const average =
         //.reduce(...) loops over reviews array. sum (1st argument) will be the total of all reviews. r.rating is the actual rating
        reviews.reduce((sum, r) => sum + Number(r.rating), 0) / total;
        
    const reviewSchema = {
        "@context": "https://schema.org",
        "@type": "MusicGroup", // or "MusicGroup" or "LocalBusiness"
        "name": "Dirty Yachtin' Scoundrels",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": average.toFixed(1),
            "reviewCount": total,
            "bestRating": 5
        },
        "review": reviews.map((r) => ({
            "@type": "Review",
            "author": {
                "@type": "Person",
                "name": r.name
            },
            "reviewBody": r.review,
            "reviewRating": {
                "@type": "Rating",
                "ratingValue": r.rating,
                "bestRating": 5
            }
        }))
    };

    if (loading) return <p>Loading reviews...</p>;
    if (error) return <p>Error loading reviews: {error}</p>;

    return (
        <section className="reviews-carousel-container" style={{ width: slideWidth * visibleCount, overflow: 'hidden', margin: '0 auto' }}>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(reviewSchema)}
                </script>
            </Helmet>

            <h2 className="reviews-carousel-title">What Fans Are Saying</h2>

            <div
                className="reviews-carousel-slider"
                style={{
                    display: 'flex',
                    transition: 'transform 0.5s ease-in-out',
                    transform: `translateX(-${current * slideWidth}px)`,
                }}
            >
                {displayedReviews.map((r, idx) => (
                    <div key={idx} className="review-card" style={{ flexShrink: 0, width: slideWidth - 20 }}>
                        <img src={r.image} alt={r.name} className="reviewer-image" />
                        <h3 className="reviewer-name">{r.name}</h3>
                        <div className="review-stars">{renderStars(r.rating)}</div>
                        <p className="review-text">{r.review}</p>
                    </div>
                ))}
            </div>

            {reviews.length > 1 && (
                <div className="reviews-carousel-nav">
                    <button onClick={prev} className="carousel-btn prev-btn" aria-label="Previous Review">‹</button>
                    <button onClick={next} className="carousel-btn next-btn" aria-label="Next Review">›</button>
                </div>
            )}
        </section>
    );
};

export default ReviewsCarousel;
